// skipcq: JS-W1028, JS-0128
import React, { useCallback, useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Snackbar from '@mui/material/Snackbar';
import Alert from '../../components/widgets/Alert';
import useBreweryBeers from '../../hooks/useBreweryBeers';
import useBeerStyles from '../../hooks/useBeerStyles';
import useEventDefinitionsList from '../../hooks/useEventDefinitionsList';
import { useBeerDelete } from '../../hooks/useBeerDelete';
import { useBeerMerge } from '../../hooks/useBeerMerge';
import BeerListDisplay from './BeerListDisplay';
import AddBeersToEventDialog from '../beer/AddBeersToEventDialog';
import AddBeerDialog from '../../components/beer/AddBeerDialog';
import BeerProfileShadowComparisonDialog from '../../components/beer/BeerProfileShadowComparisonDialog';
import MergeBeersDialog from '../../components/beer/MergeBeersDialog';
import useBreweryGroupSiblingBeers from '../../hooks/useBreweryGroupSiblingBeers';
import { useBreweryGroupMembers } from '../../hooks/useBeerAvailability';
import useBreweryBeerShadowProfile from '../../hooks/useBreweryBeerShadowProfile';
import useBreweryBeerShadowDiscoverability from '../../hooks/useBreweryBeerShadowDiscoverability';
import { formatBreweryMemberLabel } from '../../utils/brewery/formatBreweryMemberLabel';
import {
  BREWERY_FILTER_ANY,
  beerMatchesBreweryFilter,
  breweryBeerFilterStorageKey,
  readBreweryFilterFromSession,
  writeBreweryFilterToSession,
} from '../../utils/brewery/breweryBeerFilter';

/**
 * BreweryBeersSection
 * Displays beers associated with a brewery in card format.
 */
function BreweryBeersSection({
  parentBreweryId,
  breweryName,
  breweryGroupId,
}) {
  const { beers, loading, error, refresh } = useBreweryBeers(parentBreweryId);
  const {
    siblingBeers,
    loading: siblingBeersLoading,
    refresh: refreshSiblingBeers,
  } = useBreweryGroupSiblingBeers(breweryGroupId, parentBreweryId);
  const { members: groupMembers } = useBreweryGroupMembers(breweryGroupId);
  const { beerStyles } = useBeerStyles();
  const {
    events: eventList,
    loading: eventsLoading,
    error: eventsError,
    appendFeaturedBeersToEvent,
  } = useEventDefinitionsList();
  const [selectedBeerIds, setSelectedBeerIds] = useState([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteResult, setDeleteResult] = useState(null);
  const [successOpen, setSuccessOpen] = useState(false);
  const [errorOpen, setErrorOpen] = useState(false);
  const [editBeerDialogOpen, setEditBeerDialogOpen] = useState(false);
  const [editingBeer, setEditingBeer] = useState(null);
  const [mergeDialogOpen, setMergeDialogOpen] = useState(false);
  const [mergeSuccessOpen, setMergeSuccessOpen] = useState(false);
  const [mergeResult, setMergeResult] = useState(null);
  const [addToEventOpen, setAddToEventOpen] = useState(false);
  const [addToEventSnackbar, setAddToEventSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const [addBeerDialogOpen, setAddBeerDialogOpen] = useState(false);
  const [addBeerSnackbar, setAddBeerSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success',
  });
  const [hiddenBeerIds, setHiddenBeerIds] = useState([]);
  const [optimisticBeerUpdates, setOptimisticBeerUpdates] = useState({});

  const normalizedGroupId =
    typeof breweryGroupId === 'string' ? breweryGroupId.trim() : '';
  const filterStorageKey = normalizedGroupId
    ? breweryBeerFilterStorageKey(normalizedGroupId)
    : '';

  const groupMemberIds = useMemo(
    () => groupMembers.map((member) => member.id),
    [groupMembers],
  );

  const [breweryFilterValue, setBreweryFilterValue] = useState(parentBreweryId);

  useEffect(() => {
    if (!normalizedGroupId || !parentBreweryId) {
      setBreweryFilterValue(parentBreweryId);
      return;
    }
    const resolved = readBreweryFilterFromSession(filterStorageKey, {
      parentBreweryId,
      groupMemberIds,
    });
    setBreweryFilterValue(resolved);
  }, [normalizedGroupId, parentBreweryId, filterStorageKey, groupMemberIds]);

  const refreshBeerLists = useCallback(async () => {
    const tasks = [refresh()];
    if (normalizedGroupId) {
      tasks.push(refreshSiblingBeers());
    }
    await Promise.all(tasks);
  }, [refresh, refreshSiblingBeers, normalizedGroupId]);

  const { deleteBeers, loading: deleteLoading, errorMessage: deleteError } =
    useBeerDelete();
  const { mergeBeers, loading: mergeLoading, errorMessage: mergeError } =
    useBeerMerge();

  // Create lookup map for beer styles (id -> name)
  const styleMap = React.useMemo(() => {
    const map = {};
    beerStyles.forEach((style) => {
      map[style.id] = style.name || style.id;
    });
    return map;
  }, [beerStyles]);

  const handleToggleBeerSelect = useCallback((beerId, selected) => {
    if (!beerId) return;
    setSelectedBeerIds((prev) => {
      if (selected) {
        if (prev.includes(beerId)) {
          return prev;
        }
        return [...prev, beerId];
      }
      return prev.filter((id) => id !== beerId);
    });
  }, []);

  const handleOpenAddToEvent = useCallback(() => {
    setAddToEventOpen(true);
  }, []);

  const handleOpenAddBeer = useCallback(() => {
    setAddBeerDialogOpen(true);
  }, []);

  const handleCloseAddBeer = useCallback(() => {
    setAddBeerDialogOpen(false);
  }, []);

  const handleAddBeerSuccess = useCallback(
    async (payload = {}) => {
      if (payload.outcome === 'updated') {
        setAddBeerSnackbar({
          open: true,
          message: `Beer updated: ${payload.beerName ?? ''} (${payload.beerId ?? ''})`,
          severity: 'success',
        });
      } else {
        setAddBeerSnackbar({
          open: true,
          message: 'Beer added.',
          severity: 'success',
        });
      }
      try {
        await refreshBeerLists();
      } catch (refreshError) {
        console.error('Failed to refresh brewery beers after add:', refreshError);
      }
    },
    [refreshBeerLists],
  );

  const handleCloseAddBeerSnackbar = useCallback((event, reason) => {
    if (reason === 'clickaway') return;
    setAddBeerSnackbar((prev) => ({ ...prev, open: false }));
  }, []);

  const handleCloseAddToEvent = useCallback(() => {
    setAddToEventOpen(false);
  }, []);

  const handleCloseAddToEventSnackbar = useCallback((event, reason) => {
    if (reason === 'clickaway') return;
    setAddToEventSnackbar((prev) => ({ ...prev, open: false }));
  }, []);

  const handleConfirmAddToEvent = useCallback(
    async (eventId, beerIds) => {
      try {
        await appendFeaturedBeersToEvent(eventId, beerIds);
        setSelectedBeerIds([]);
        setAddToEventSnackbar({
          open: true,
          message: 'Beers added to the event.',
          severity: 'success',
        });
      } catch (appendError) {
        setAddToEventSnackbar({
          open: true,
          message: 'Could not add beers to the event. Try again.',
          severity: 'error',
        });
        throw appendError;
      }
    },
    [appendFeaturedBeersToEvent],
  );

  const handleOpenDeleteDialog = useCallback(() => {
    setDeleteDialogOpen(true);
  }, []);

  const handleCloseDeleteDialog = useCallback(() => {
    setDeleteDialogOpen(false);
    setDeleteResult(null);
  }, []);

  const handleConfirmDeleteBeers = useCallback(async () => {
    if (!selectedBeerIds.length) return;

    setDeleteResult(null);
    setErrorOpen(false);

    const result = await deleteBeers(selectedBeerIds);

    if (result?.deleted && result.deletedCount > 0) {
      const failedBeerIds = new Set(
        (Array.isArray(result.failedBeers) ? result.failedBeers : [])
          .map((failedBeer) => failedBeer.beerId)
          .filter(Boolean),
      );
      const deletedIds = selectedBeerIds.filter(
        (beerId) => !failedBeerIds.has(beerId),
      );
      setHiddenBeerIds((prev) => Array.from(new Set([...prev, ...deletedIds])));
      try {
        await refreshBeerLists();
        setHiddenBeerIds((prev) => prev.filter((id) => !deletedIds.includes(id)));
      } catch (refreshError) {
        console.error('Failed to refresh beers after delete:', refreshError);
      }
      setSelectedBeerIds([]);
      setDeleteResult({
        status: result.status || '',
        deletedCount: result.deletedCount,
        failedCount: result.failedCount,
        failedBeers: result.failedBeers,
      });
      setSuccessOpen(true);
      setDeleteDialogOpen(false);
    } else if (result?.error) {
      setErrorOpen(true);
      setDeleteDialogOpen(false);
    } else if (result?.failedCount > 0) {
      // All beers failed to delete (e.g., all have reviews/favorites)
      setDeleteResult({
        status: result.status || '',
        deletedCount: 0,
        failedCount: result.failedCount,
        failedBeers: result.failedBeers,
      });
      setSuccessOpen(true); // Shows failure details in snackbar
      setDeleteDialogOpen(false);
    }
  }, [selectedBeerIds, refreshBeerLists, deleteBeers]);

  const handleCloseSuccessSnackbar = useCallback(() => {
    setSuccessOpen(false);
    setDeleteResult(null);
  }, []);

  const handleCloseErrorSnackbar = useCallback(() => {
    setErrorOpen(false);
  }, []);

  const handleEditBeer = useCallback((beer) => {
    setEditingBeer(beer);
    setEditBeerDialogOpen(true);
  }, []);

  const handleCloseEditBeerDialog = useCallback(() => {
    setEditBeerDialogOpen(false);
    setEditingBeer(null);
  }, []);

  const handleEditBeerSuccess = useCallback(async () => {
    try {
      await refreshBeerLists();
    } catch (refreshError) {
      console.error('Failed to refresh beers after edit:', refreshError);
    } finally {
      handleCloseEditBeerDialog();
    }
  }, [refreshBeerLists, handleCloseEditBeerDialog]);

  const deleteDisabled = !selectedBeerIds.length || deleteLoading;
  const mergeDisabled = selectedBeerIds.length !== 2 || mergeLoading;

  const displayBeers = useMemo(() => {
    const beersById = new Map();
    beers.forEach((beer) => beersById.set(beer.id, beer));
    siblingBeers.forEach((beer) => {
      if (!beersById.has(beer.id)) {
        beersById.set(beer.id, beer);
      }
    });

    const hiddenBeerIdSet = new Set(hiddenBeerIds);
    const rows = Array.from(beersById.values())
      .filter((beerRow) => !hiddenBeerIdSet.has(beerRow.id))
      .map((beerRow) => {
        const patch = optimisticBeerUpdates[beerRow.id];
        return patch ? { ...beerRow, ...patch } : beerRow;
      });

    return rows.sort((left, right) =>
      (left.name || '').localeCompare(right.name || '', 'en', {
        sensitivity: 'base',
      }),
    );
  }, [beers, siblingBeers, hiddenBeerIds, optimisticBeerUpdates]);

  const showBreweryFilter =
    Boolean(normalizedGroupId) && groupMembers.length > 0;

  const locationFilteredBeers = useMemo(() => {
    if (!showBreweryFilter) {
      return displayBeers;
    }
    return displayBeers.filter((beerRow) =>
      beerMatchesBreweryFilter(beerRow, breweryFilterValue),
    );
  }, [displayBeers, breweryFilterValue, showBreweryFilter]);

  const {
    onShadowProfileClick,
    shadowProfileDialogOpen,
    shadowProfileBeerId,
    shadowProfileBeerName,
    handleCloseShadowProfileDialog,
  } = useBreweryBeerShadowProfile();

  const {
    filteredBeers,
    shadowDiscoverabilityControl,
    shadowSummariesByBeerId,
  } = useBreweryBeerShadowDiscoverability(
    locationFilteredBeers,
    Boolean(onShadowProfileClick),
  );

  const breweryNameById = useMemo(() => {
    const map = {};
    groupMembers.forEach((member) => {
      map[member.id] = formatBreweryMemberLabel(member);
    });
    return map;
  }, [groupMembers]);

  const groupedBreweryIds = useMemo(() => {
    if (!normalizedGroupId) {
      return null;
    }
    const map = {};
    groupMembers.forEach((member) => {
      map[member.id] = true;
    });
    return map;
  }, [groupMembers, normalizedGroupId]);

  const handleBreweryFilterChange = useCallback(
    (event) => {
      const nextValue = event.target.value;
      setBreweryFilterValue(nextValue);
      writeBreweryFilterToSession(filterStorageKey, nextValue);
    },
    [filterStorageKey],
  );

  const currentLocationFilterLabel = useMemo(() => {
    const currentMember = groupMembers.find(
      (member) => member.id === parentBreweryId,
    );
    if (currentMember) {
      return formatBreweryMemberLabel(currentMember);
    }
    return breweryName || 'Current location';
  }, [groupMembers, parentBreweryId, breweryName]);

  const breweryFilterControl = showBreweryFilter ? (
    <FormControl sx={{ minWidth: 220, mx: 0.5 }}>
      <InputLabel id="brewery-beers-filter-label">Breweries</InputLabel>
      <Select
        labelId="brewery-beers-filter-label"
        id="brewery-beers-filter"
        value={breweryFilterValue}
        label="Breweries"
        onChange={handleBreweryFilterChange}
      >
        <MenuItem value={parentBreweryId}>{currentLocationFilterLabel}</MenuItem>
        <MenuItem value={BREWERY_FILTER_ANY}>Any</MenuItem>
        {groupMembers
          .filter((member) => member.id !== parentBreweryId)
          .map((member) => (
            <MenuItem key={member.id} value={member.id}>
              {formatBreweryMemberLabel(member)}
            </MenuItem>
          ))}
      </Select>
    </FormControl>
  ) : null;

  const mergeBeersPool = displayBeers;

  // Get the two selected beers for merging
  const selectedBeersForMerge = React.useMemo(() => {
    if (selectedBeerIds.length !== 2) return [null, null];
    const beer1 = mergeBeersPool.find((beerRow) => beerRow.id === selectedBeerIds[0]);
    const beer2 = mergeBeersPool.find((beerRow) => beerRow.id === selectedBeerIds[1]);
    return [beer1 || null, beer2 || null];
  }, [selectedBeerIds, mergeBeersPool]);

  const handleOpenMergeDialog = useCallback(() => {
    if (selectedBeerIds.length === 2) {
      setMergeDialogOpen(true);
    }
  }, [selectedBeerIds]);

  const handleCloseMergeDialog = useCallback(() => {
    setMergeDialogOpen(false);
  }, []);

  const handleMergeBeers = useCallback(
    async ({ breweryId, survivingBeerId, mergingBeerId, mergedBeer }) => {
      const [beer1, beer2] = selectedBeersForMerge;
      
      const result = await mergeBeers({
        breweryId,
        survivingBeerId,
        mergingBeerId,
        mergedBeer,
        beer1: beer1 ? {
          name: beer1.name,
          style: beer1.style,
          abv: beer1.abv,
          description: beer1.description,
        } : null,
        beer2: beer2 ? {
          name: beer2.name,
          style: beer2.style,
          abv: beer2.abv,
          description: beer2.description,
        } : null,
      });

      if (result?.merged) {
        setHiddenBeerIds((prev) =>
          prev.includes(mergingBeerId) ? prev : [...prev, mergingBeerId],
        );
        setOptimisticBeerUpdates((prev) => ({
          ...prev,
          [survivingBeerId]: {
            name: mergedBeer.name,
            style: mergedBeer.style,
            abv: mergedBeer.abv,
            ...(mergedBeer.description !== undefined && {
              description: mergedBeer.description,
            }),
          },
        }));
        setMergeResult(result);
        setMergeDialogOpen(false);
        setMergeSuccessOpen(true);
        setSelectedBeerIds([]);
        try {
          await refreshBeerLists();
          setHiddenBeerIds((prev) => prev.filter((beerId) => beerId !== mergingBeerId));
          setOptimisticBeerUpdates((prev) => {
            const next = { ...prev };
            delete next[survivingBeerId];
            return next;
          });
        } catch (refreshError) {
          console.error('Failed to refresh beers after merge:', refreshError);
        }
      }
      // Error handling is done by the hook (errorMessage state)
    },
    [mergeBeers, refreshBeerLists, selectedBeersForMerge],
  );

  const handleCloseMergeSuccessSnackbar = useCallback(() => {
    setMergeSuccessOpen(false);
    setMergeResult(null);
  }, []);

  return (
    <>
      <BeerListDisplay
        beers={filteredBeers}
        loading={loading || siblingBeersLoading}
        error={error}
        styleMap={styleMap}
        onAddBeer={handleOpenAddBeer}
        selectedBeerIds={selectedBeerIds}
        onToggleBeerSelect={handleToggleBeerSelect}
        onDeleteClick={handleOpenDeleteDialog}
        deleteDisabled={deleteDisabled}
        onEditBeer={handleEditBeer}
        onMergeClick={handleOpenMergeDialog}
        mergeDisabled={mergeDisabled}
        breweryNameById={breweryNameById}
        groupedBreweryIds={groupedBreweryIds}
        breweryFilterControl={breweryFilterControl}
        shadowDiscoverabilityControl={shadowDiscoverabilityControl}
        shadowProfileSummariesByBeerId={shadowSummariesByBeerId}
        onAddToEvent={handleOpenAddToEvent}
        addToEventDisabled={!selectedBeerIds.length}
        onShadowProfileClick={onShadowProfileClick}
      />
      <BeerProfileShadowComparisonDialog
        open={shadowProfileDialogOpen}
        beerId={shadowProfileBeerId}
        beerName={shadowProfileBeerName}
        onClose={handleCloseShadowProfileDialog}
      />
      <AddBeersToEventDialog
        open={addToEventOpen}
        onClose={handleCloseAddToEvent}
        events={eventList}
        eventsLoading={eventsLoading}
        eventsError={eventsError}
        selectedBeerIds={selectedBeerIds}
        onConfirmAdd={handleConfirmAddToEvent}
      />
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        aria-labelledby="delete-beers-dialog-title"
        aria-describedby="delete-beers-dialog-description"
      >
        <DialogTitle id="delete-beers-dialog-title">
          Delete Beers
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-beers-dialog-description">
            Are you sure you want to delete{' '}
            {selectedBeerIds.length > 1
              ? `${selectedBeerIds.length} selected beers`
              : 'the selected beer'}
            ? Beers with favorites or reviews cannot be deleted. This action
            cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog} color="primary">
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDeleteBeers}
            color="error"
            variant="contained"
            disabled={deleteLoading}
            startIcon={
              deleteLoading ? (
                <CircularProgress size={20} color="inherit" />
              ) : null
            }
          >
            {deleteLoading ? 'Deleting...' : 'Delete Beers'}
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={successOpen}
        autoHideDuration={8000}
        onClose={handleCloseSuccessSnackbar}
      >
        <Alert
          onClose={handleCloseSuccessSnackbar}
          severity="success"
          sx={{ width: '100%' }}
        >
          {deleteResult && (
            <Box>
              <Typography variant="body2" component="div">
                {deleteResult.status || `Deleted ${deleteResult.deletedCount} beer${deleteResult.deletedCount !== 1 ? 's' : ''}`}
              </Typography>
              {deleteResult.failedCount > 0 && (
                <Box sx={{
                  mt: 1
                }}>
                  <Typography variant="body2" component="div">
                    Failed to delete {deleteResult.failedCount} beer
                    {deleteResult.failedCount !== 1 ? 's' : ''}:
                  </Typography>
                  <Box component="ul" sx={{ mt: 0.5, pl: 2, mb: 0 }}>
                    {(Array.isArray(deleteResult.failedBeers)
                      ? deleteResult.failedBeers
                      : []
                    ).map((failedBeer) => {
                      const beer = beers.find((b) => b.id === failedBeer.beerId);
                      const beerName = beer?.name || failedBeer.beerId;
                      return (
                        <li key={failedBeer.beerId}>
                          <Typography variant="caption" component="span">
                            {beerName}: {failedBeer.reason}
                          </Typography>
                        </li>
                      );
                    })}
                  </Box>
                </Box>
              )}
            </Box>
          )}
        </Alert>
      </Snackbar>
      <Snackbar
        open={errorOpen}
        autoHideDuration={6000}
        onClose={handleCloseErrorSnackbar}
      >
        <Alert
          onClose={handleCloseErrorSnackbar}
          severity="error"
          sx={{ width: '100%' }}
        >
          {deleteError || 'An error occurred while deleting beers. Please try again.'}
        </Alert>
      </Snackbar>
      <Snackbar
        open={addToEventSnackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseAddToEventSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseAddToEventSnackbar}
          severity={addToEventSnackbar.severity}
        >
          {addToEventSnackbar.message}
        </Alert>
      </Snackbar>
      <AddBeerDialog
        open={editBeerDialogOpen}
        mode="edit"
        editBeer={editingBeer}
        lockedBreweryId={parentBreweryId}
        lockedBreweryName={breweryName}
        breweryGroupId={normalizedGroupId || null}
        onClose={handleCloseEditBeerDialog}
        onSuccess={handleEditBeerSuccess}
      />
      <AddBeerDialog
        open={addBeerDialogOpen}
        onClose={handleCloseAddBeer}
        lockedBreweryId={parentBreweryId}
        lockedBreweryName={breweryName}
        breweryGroupId={normalizedGroupId || null}
        onSuccess={handleAddBeerSuccess}
      />
      <MergeBeersDialog
        open={mergeDialogOpen}
        onClose={handleCloseMergeDialog}
        beer1={selectedBeersForMerge[0]}
        beer2={selectedBeersForMerge[1]}
        styleMap={styleMap}
        breweryId={parentBreweryId}
        onMerge={handleMergeBeers}
        loading={mergeLoading}
        error={mergeError}
      />
      <Snackbar
        open={mergeSuccessOpen}
        autoHideDuration={8000}
        onClose={handleCloseMergeSuccessSnackbar}
      >
        <Alert
          onClose={handleCloseMergeSuccessSnackbar}
          severity="success"
          sx={{ width: '100%' }}
        >
          {mergeResult && (
            <Box>
              <Typography variant="body2" component="div">
                {mergeResult.status || 'Beers merged successfully'}
              </Typography>
              <Typography variant="caption" component="div" sx={{ mt: 0.5 }}>
                Favorites reassigned: {mergeResult.favoritesReassigned || 0} • Reviews
                reassigned: {mergeResult.reviewsReassigned || 0}
              </Typography>
            </Box>
          )}
        </Alert>
      </Snackbar>
      <Snackbar
        open={addBeerSnackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseAddBeerSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleCloseAddBeerSnackbar}
          severity={addBeerSnackbar.severity}
        >
          {addBeerSnackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}

BreweryBeersSection.propTypes = {
  parentBreweryId: PropTypes.string.isRequired,
  breweryName: PropTypes.string,
  breweryGroupId: PropTypes.string,
};

export default BreweryBeersSection;

